import { useCallback, useEffect, useRef, useState } from 'react';
import type { Match } from '@sfc/sdk';
import { platform } from '../api/client';

export type MatchesState =
  | { status: 'loading' }
  | { status: 'unreachable' }
  | { status: 'ready'; matches: Match[] };

/**
 * The matches, which are also the conversations.
 *
 * Held above the screens that use them for the same reason the inbox is: the
 * number of unread messages is a badge on the navigation as well as a line in
 * the list, and two copies fetched separately disagree the moment anybody
 * writes anything.
 */
export function useMatches(): {
  state: MatchesState;
  unread: number;
  refresh: () => void;
} {
  const [state, setState] = useState<MatchesState>({ status: 'loading' });
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const matches = await platform.match.matches();
      if (live.current) setState({ status: 'ready', matches });
    } catch {
      if (live.current) setState({ status: 'unreachable' });
    }
  }, []);

  useEffect(() => {
    void load();

    // Messages arrive while nobody is looking. Asked again when the app comes
    // back to the front, rather than on a timer that would ask all day.
    const again = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('focus', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [load]);

  const unread =
    state.status === 'ready' ? state.matches.reduce((total, m) => total + m.unread, 0) : 0;

  return { state, unread, refresh: load };
}
